/**
 * Rota de cada módulo e o módulo de que ele é assunto, para a trilha de navegação.
 *
 * Papel: aqui fica `MODULOS`, a lista que o `Layout` usa para descobrir, pelo
 * endereço atual, o nome do módulo mostrado na trilha ao lado da marca. A
 * ordem dela é a de `CHAVES_MODULO`, a mesma das linhas na grade de
 * permissões — a ordem dos cartões da tela inicial é outra, e vive em
 * `cartoes-da-tela-inicial.ts`.
 *
 * Como usar: quando a tela de um módulo ganha endereço no `router.tsx`, a
 * entrada dele aqui deverá receber, na mesma alteração, `rota` com o mesmo
 * caminho. É este campo que `infoDaTrilha`, em `layout.tsx`, compara com o
 * começo do endereço atual para achar o módulo da tela aberta; uma rota que
 * não bater ali deixa a trilha simplesmente de fora daquela tela, sem nenhum
 * aviso.
 *
 * O nome do módulo não é escrito aqui: vem de `ROTULOS_MODULO`, pela `chave`,
 * porque a grade de permissões mostra o mesmo nome e mora num módulo que não
 * alcança `app/`. A `chave` tipada como `ChaveModulo` faz uma grafia divergente
 * de `CHAVES_MODULO` virar erro de compilação, em vez de um módulo que some
 * sem aviso da trilha justamente para quem tem permissão nele.
 *
 * `assuntoDe` é o que faz a trilha do Calendário e das Manutenções da Fazenda
 * Boa Vista mostrar "Residências" em vez do próprio nome: os dois vivem como
 * submenu dentro da tela de Residências, e não como módulo com trilha própria.
 * `infoDaTrilha`, no `layout.tsx`, lê este campo para decidir o rótulo e se o
 * último item da trilha é um link — nenhuma outra parte do código compara rota
 * por rota para chegar à mesma decisão.
 *
 * Arquivo de dados puro, sem import de componente, para poder ser lido por
 * testes e scripts sem montar React.
 */
import { type ChaveModulo } from '@concierge/shared';

/** Descrição de um módulo para a trilha de navegação do `Layout`. */
export interface Modulo {
  /** Chave do contrato, a mesma de `CHAVES_MODULO` e das permissões. */
  readonly chave: ChaveModulo;
  /** Caminho absoluto da rota do módulo. */
  readonly rota: string;
  /**
   * Chave do módulo do qual esta tela é assunto, quando ela não tem trilha
   * própria. Omitido, o próprio módulo é o que a trilha nomeia.
   */
  readonly assuntoDe?: ChaveModulo;
}

/**
 * Os módulos, na ordem de `CHAVES_MODULO`.
 *
 * A ordem desta lista deverá acompanhar a de `CHAVES_MODULO`, e
 * `modulos.test.ts` falha quando as duas divergem. A grade de permissões não lê
 * esta lista, e sim `MODULOS_CONCEDIVEIS`: trocar a posição de um módulo só aqui
 * derruba o teste sem mudar tela nenhuma, e reposicionar uma linha da grade
 * exige mexer em `CHAVES_MODULO` e repetir a mesma troca aqui.
 *
 * O link de `usuarios`, na moldura, e o cartão dele, na tela inicial, só
 * aparecem para administrador: o `Layout` e a `PaginaInicial` filtram por
 * `acessoAlcanca`, e a grade de permissões não concede esse módulo a ninguém.
 */
export const MODULOS: readonly Modulo[] = [
  { chave: 'financas', rota: '/financas' },
  { chave: 'rh', rota: '/rh' },
  { chave: 'veiculos', rota: '/veiculos' },
  { chave: 'residencias', rota: '/residencias' },
  { chave: 'calendario-boa-vista', rota: '/calendario', assuntoDe: 'residencias' },
  { chave: 'manutencoes-boa-vista', rota: '/manutencoes', assuntoDe: 'residencias' },
  { chave: 'orcamentos', rota: '/orcamentos' },
  { chave: 'compras', rota: '/compras' },
  { chave: 'mailing', rota: '/mailing' },
  { chave: 'saude', rota: '/saude' },
  { chave: 'documentos', rota: '/documentos' },
  { chave: 'cadastros-base', rota: '/cadastros-base' },
  { chave: 'solicitacao-de-compra', rota: '/solicitar-compra' },
  { chave: 'usuarios', rota: '/usuarios' },
] as const;
